import * as React from 'react'
import controlStore from '../../store/controlStore.ts'
import drawerStore from '../../store/drawerStore.ts'
import historyStore from '../../store/historyStore.ts'
import mapStore from '../../store/mapStore.ts'
import { updatedStyles } from '../map/components/GoogleMap/mapStyles.ts'
import { POI_OFF } from '../map/components/GoogleMap/GoogleMapWrapper.tsx'

import '../map/map.css'
import { HistoryList } from './components/list/HistoryList.tsx'
import { ClusterMarker } from './components/markers/ClusterMarker.tsx'
import { HighlightMarker } from './components/markers/HighlightMarker.tsx'
import { HistoryControl } from './HistoryControl.tsx'
import { AnimatedMarker } from './components/movie/AnimatedMarker.tsx'
import { IPet, IPoint } from '../../interfaces/types.ts'
import movieStore from '../../store/movieStore.ts'
import { useLocation } from 'react-router-dom'
import { PetDrawer } from '../petDrawer/PetDrawer.tsx'
import { MiniPetList } from '../petDrawer/MiniPetList.tsx'
import { MapTypeToggle } from '../common/MapTypeToggle.tsx'
import { CenterMapButton } from '../common/buttons/CenterMapButton.tsx'
import { useLatest } from '../../hooks/useLatest.ts'
import { SafeZones } from '../safeZones/SafeZones.tsx'
import { DayHistoryMap } from './components/dayHistory/DayHistoryMap.tsx'
import { DayHistoryPanel } from './components/dayHistory/DayHistoryPanel.tsx'
import useDayHistoryStore from '../../store/dayHistoryStore.ts'


export const HistoryMap: React.FC = () => {
    const location = useLocation()

    const map = historyStore((s) => s.map)
    const listRef = React.useRef(null)
    const controlRef = React.useRef(null)

    const mapStyle = mapStore(s => s.mapStyle)
    const isMobile = controlStore(s => s.isOnMobileDevice)
    const isMovieMode = historyStore(s => s.isMovieMode)
    const showSafeZones = historyStore(s => s.showSafeZones)
    const dayHistoryActive = useDayHistoryStore(s => s.active)

    // Keep latest values available inside the stable resize handler
    const mapStyleRef = useLatest(mapStyle)
    const isMovieModeRef = useLatest(isMovieMode)
    const mapRef = useLatest(map)

    // Stable resize handler (created once) — reads refs for current values
    const handleResizeRef = React.useRef<() => void>()
    if (!handleResizeRef.current) {
        handleResizeRef.current = () => {
            setTimeout(() => {
                const currentMap = mapRef.current
                const currentMapStyle = mapStyleRef.current
                const styles = updatedStyles[currentMapStyle]
                    ? [...(updatedStyles[currentMapStyle] as []), POI_OFF]
                    : updatedStyles.default
                currentMap?.setOptions({ styles })

                const bounds = new google.maps.LatLngBounds()
                let update = false
                if (!isMovieModeRef.current) {
                    const locations = historyStore.getState().locations?.filter(l => l?.properties?.status) || []
                    locations.forEach((point: IPoint) => {
                        bounds.extend(new google.maps.LatLng({
                            lat: point?.properties?.latitude,
                            lng: point?.properties?.longitude,
                        }))
                        update = true
                    })
                } else {
                    const locations = movieStore.getState().locations?.filter(l => l?.properties?.status) || []
                    if (locations?.[0]?.properties) {
                        bounds.extend(new google.maps.LatLng({
                            lat: locations[0]?.properties?.latitude,
                            lng: locations[0]?.properties?.longitude,
                        }))
                        update = true
                    }
                }
                if (update) {
                    historyStore.getState().map?.fitBounds(bounds)
                }
            }, 200)
        }
    }

    React.useEffect(() => {
        mapStore.getState().setMapContext('history')
        controlStore.getState().setCurrentPage('history')
        const pet = location?.state?.pet as IPet
        historyStore.getState().setSelectedPetId(pet?._id)

        isMobile ? drawerStore.getState().setChildren(<MiniPetList/>) : drawerStore.getState().setChildren(null)

        return () => {
            // Use the same handler reference we attached below
            window.removeEventListener('resize', handleResizeRef.current!)
            historyStore.getState().setLocations([])
            historyStore.getState().setHistoryPerPet({})
            historyStore.getState().setMarkers([])
            historyStore.getState().setIsMovieMode(false)
            historyStore.getState().setSelectedPetId('')
            useDayHistoryStore.getState().reset()
        }
    }, [])

    React.useEffect(() => {
        if (map) {
            const listener = window.google.maps.event.addListener(map, 'tilesloaded', function () {
                const styles = updatedStyles[mapStyle]
                    ? [...(updatedStyles[mapStyle] as []), POI_OFF]
                    : updatedStyles.default
                map?.setOptions({ styles })
                window.google.maps.event.removeListener(listener)
                window.addEventListener('resize', handleResizeRef.current!)
                if (!isMobile) {
                    window.dispatchEvent(new Event('resize'))
                }
            })
            const styles = updatedStyles[mapStyle]
                ? [...(updatedStyles[mapStyle] as []), POI_OFF]
                : updatedStyles.default
            map?.setOptions({ styles })
        }
    }, [map])


    const googleMap = (
        <React.Fragment>
            {dayHistoryActive ? (
                <DayHistoryMap />
            ) : (
                <>
                    {!isMobile && isMovieMode ? <AnimatedMarker/> : <ClusterMarker/>}
                    <HighlightMarker/>
                </>
            )}
            {showSafeZones && !dayHistoryActive && <SafeZones/>}
            {isMobile ? (
                <div className={'absolute bottom-0 z-10 pointer-events-auto'}>
                    <PetDrawer/>
                </div>
            ) : null}
            <div className={`absolute ${!isMobile ? 'bottom-[8rem] right-4' : 'bottom-[2rem] right-5'} pointer-events-auto`}>
                <CenterMapButton history/>
            </div>
        </React.Fragment>
    )

    const historyList = React.useMemo(() => <HistoryList listRef={listRef}/>, [])

    const mobileMap = (
        <div className="pointer-events-none w-full h-full flex flex-col">
            <div id={'history-map-mobile'} className={'mainMap relative'}>
                <div className={'absolute z-10 top-2 pointer-events-auto w-full'}>
                    {dayHistoryActive ? (
                        <div className={'bg-base-100 rounded-b-xl shadow-md mx-1'}>
                            <DayHistoryPanel />
                        </div>
                    ) : (
                        <HistoryControl controlRef={controlRef} noPets={isMobile} showLabel/>
                    )}
                </div>
                {googleMap}
            </div>
        </div>
    )

    const displayMap = (
        <div className="pointer-events-none w-full h-full flex flex-col">
            <div className="w-full bg-base-100 z-10 pointer-events-auto pt-2 pb-2 border-b border-base-300">
                {dayHistoryActive ? <DayHistoryPanel /> : historyList}
            </div>
            <div className="relative flex-1 w-full pointer-events-none">
                <div id={'history-map'} className={'mainMap absolute inset-0 pointer-events-none'}>
                    <div className="pointer-events-auto flex items-start justify-between">
                        <div className="mt-2 ml-2">
                            <MapTypeToggle />
                        </div>
                        <HistoryControl controlRef={controlRef} noPets={isMobile} showLabel/>
                    </div>
                </div>
                {googleMap}
            </div>
        </div>
    )

    return isMobile ? mobileMap : displayMap
}
